import React, { useState, useEffect, useCallback, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import useEmblaCarousel from 'embla-carousel-react';
import { useNavigate } from 'react-router-dom';
import { Dialog, Transition } from '@headlessui/react';
import Button from '../ui/Button';
import { useAuth } from '../../context/AuthContext';
import { updateMe } from '../../features/account/data/user.service';
import CreatePetDialog from '../../features/pets/Dialogs/CreatePetDialog';

// Utility for conditional classes
function cn(...classes) {
    return classes.filter(Boolean).join(' ');
}

export default function OnboardingWizard() {
    const [isOpen, setIsOpen] = useState(false);
    const [isPetDialogOpen, setIsPetDialogOpen] = useState(false);
    const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false });
    const [selectedIndex, setSelectedIndex] = useState(0);
    const navigate = useNavigate();
    const { user, isAuthenticated } = useAuth();
    const { t } = useTranslation();

    // Visibility logic: controlled strictly by backend field
    useEffect(() => {
        if (isAuthenticated && user?.show_onboarding) {
            setIsOpen(true);
        }
    }, [isAuthenticated, user?.show_onboarding]);

    const onSelect = useCallback(() => {
        if (!emblaApi) return;
        setSelectedIndex(emblaApi.selectedScrollSnap());
    }, [emblaApi]);

    useEffect(() => {
        if (!emblaApi) return;
        emblaApi.on('select', onSelect);
        onSelect(); // initial call
    }, [emblaApi, onSelect]);

    const handleExplorar = useCallback(() => {
        setIsOpen(false);
        // Fire and forget server update
        updateMe({ show_onboarding: false }).catch(err => {
            console.error("Failed to sync onboarding status", err);
        });
    }, []);

    const handleAddPet = useCallback(() => {
        setIsOpen(false);
        // Fire and forget server update
        updateMe({ show_onboarding: false }).catch(err => {
            console.error("Failed to sync onboarding status", err);
        });
        // Open Pet Dialog
        setIsPetDialogOpen(true);
    }, []);

    const handleNext = () => {
        if (emblaApi) emblaApi.scrollNext();
    };

    const handleBack = () => {
        if (emblaApi) emblaApi.scrollPrev();
    };

    const steps = [
        {
            id: 'welcome',
            title: t('onboarding.welcome.title'),
            description: t('onboarding.welcome.description'),
            image: '/images/onboarding/onboarding_welcome_v3.jpg',
            color: 'bg-emerald-500'
        },
        {
            id: 'ai_search',
            title: t('onboarding.step_ai_search.title'),
            description: t('onboarding.step_ai_search.description'),
            image: '/images/onboarding/onboarding_ai_search_v4.jpg',
            color: 'bg-blue-500'
        },
        {
            id: 'care_plan',
            title: t('onboarding.step_care_plan.title'),
            description: t('onboarding.step_care_plan.description'),
            image: '/images/onboarding/onboarding_care_plan.jpg',
            color: 'bg-indigo-500'
        },
        {
            id: 'health',
            title: t('onboarding.step_health.title'),
            description: t('onboarding.step_health.description'),
            image: '/images/onboarding/onboarding_health_v2.jpg',
            color: 'bg-rose-500'
        },
        {
            id: 'community',
            title: t('onboarding.step_community.title'),
            description: t('onboarding.step_community.description'),
            image: '/images/onboarding/onboarding_community_v2.jpg',
            color: 'bg-amber-500'
        },
        {
            id: 'start',
            title: t('onboarding.step_start.title'),
            description: t('onboarding.step_start.description'),
            image: '/images/onboarding/onboarding_start_v2.jpg',
            color: 'bg-emerald-600',
            isLast: true
        }
    ];

    const isLastStep = selectedIndex === steps.length - 1;

    return (
        <Fragment>
            <Transition show={isOpen} as={Fragment}>
                <Dialog as="div" className="relative z-[100]" onClose={() => { }}>
                    {/* Backdrop */}
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
                    </Transition.Child>

                    {/* Layout Container */}
                    <div className="fixed inset-0 flex items-center justify-center p-4">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel className="relative w-full sm:max-w-md md:max-w-4xl lg:max-w-5xl bg-white dark:bg-card-dark rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">

                                {/* Content Wrapper - Adjustable Height */}
                                <div className="flex flex-col md:flex-row h-[80vh] md:h-[600px] overflow-hidden">

                                    {/* Carousel Area */}
                                    <div className="flex-1 overflow-hidden flex flex-col md:flex-row w-full h-full relative" ref={emblaRef}>
                                        <div className="flex h-full w-full">
                                            {steps.map((step) => (
                                                <div key={step.id} className="flex-[0_0_100%] min-w-0 relative flex flex-col md:flex-row h-full">

                                                    {/* Image Section */}
                                                    <div className={`relative h-[50%] sm:h-72 md:h-full w-full md:w-1/2 overflow-hidden ${step.color} bg-opacity-10`}>
                                                        <img
                                                            src={step.image}
                                                            alt={step.title}
                                                            className="w-full h-full object-cover object-center transition-transform duration-700 hover:scale-105"
                                                        />
                                                        {/* Gradient overlay mobile (bottom) - Soft smooth gradient */}
                                                        <div className="absolute bottom-0 left-0 w-full h-24 bg-gradient-to-t from-white/60 dark:from-card-dark/60 to-transparent md:hidden" />

                                                        {/* Gradient overlay desktop (right) - Soft smooth gradient */}
                                                        <div className="hidden md:block absolute top-0 right-0 h-full w-32 bg-gradient-to-l from-white/60 dark:from-card-dark/60 to-transparent" />
                                                    </div>

                                                    {/* Text Content */}
                                                    <div className="p-6 md:p-12 flex flex-col items-center md:items-start text-center md:text-left gap-4 bg-white dark:bg-card-dark flex-grow md:w-1/2 justify-start pt-8 sm:pt-6 md:justify-center md:pb-24">
                                                        <h2 className="text-xl sm:text-2xl md:text-4xl font-black text-foreground-light dark:text-foreground-dark tracking-tight leading-tight">
                                                            {step.title}
                                                        </h2>
                                                        {step.description && (
                                                            <p className="text-base md:text-lg text-subtle-light dark:text-subtle-dark font-normal leading-relaxed w-full md:max-w-md">
                                                                {step.description}
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Footer / Controls */}
                                    <div className="absolute bottom-0 left-0 w-full md:w-1/2 md:left-auto md:right-0 p-4 md:p-10 pt-0 pb-2 flex flex-col justify-end bg-white/90 dark:bg-card-dark/90 md:bg-transparent backdrop-blur-sm md:backdrop-blur-none z-30 min-h-[80px] md:min-h-0 transition-[min-height] duration-300 pointer-events-none">

                                        <div className="grid grid-cols-1 grid-rows-1 w-full items-end pointer-events-auto">
                                            {/* FINAL STEP BUTTONS */}
                                            <Transition
                                                show={isLastStep}
                                                as="div"
                                                className="col-start-1 row-start-1 w-full flex flex-col md:flex-row gap-3 md:justify-end items-center z-10"
                                            >
                                                {/* Button 1: Explorar (Mobile: Bottom, Leaves 'Fast' via Distance) */}
                                                <Transition.Child
                                                    as={Fragment}
                                                    enter="transform transition duration-500 ease-out delay-300 md:delay-100"
                                                    enterFrom="opacity-0 translate-y-12"
                                                    enterTo="opacity-100 translate-y-0"
                                                    leave="transform transition duration-500 ease-in delay-0 md:delay-100"
                                                    leaveFrom="opacity-100 translate-y-0"
                                                    leaveTo="opacity-0 translate-y-24"
                                                >
                                                    <div className="w-full md:w-auto order-2 md:order-1">
                                                        <Button
                                                            variant="neutral"
                                                            onClick={handleExplorar}
                                                            className="w-full"
                                                        >
                                                            {t('onboarding.step_start.button_explore')}
                                                        </Button>
                                                    </div>
                                                </Transition.Child>

                                                {/* Button 2: Agregar (Mobile: Top, Leaves 'Slow' via Distance) */}
                                                <Transition.Child
                                                    as={Fragment}
                                                    enter="transform transition duration-500 ease-out delay-100 md:delay-300"
                                                    enterFrom="opacity-0 translate-y-12"
                                                    enterTo="opacity-100 translate-y-0"
                                                    leave="transform transition duration-500 ease-in delay-0"
                                                    leaveFrom="opacity-100 translate-y-0"
                                                    leaveTo="opacity-0 translate-y-12"
                                                >
                                                    <div className="w-full md:w-auto order-1 md:order-2">
                                                        <Button
                                                            variant="magic"
                                                            onClick={handleAddPet}
                                                            className="w-full shadow-lg shadow-primary/20"
                                                        >
                                                            {t('onboarding.step_start.button_add_pet')}
                                                        </Button>
                                                    </div>
                                                </Transition.Child>
                                            </Transition>

                                            {/* STANDARD NAVIGATION - Single Row, No Back Button */}
                                            <Transition
                                                show={!isLastStep}
                                                as="div"
                                                className="col-start-1 row-start-1 w-full flex flex-row items-center justify-between gap-4 z-0"
                                                enter="transition-opacity duration-300 delay-300"
                                                enterFrom="opacity-0"
                                                enterTo="opacity-100"
                                                leave="transition-opacity duration-200"
                                                leaveFrom="opacity-100"
                                                leaveTo="opacity-0"
                                            >
                                                {/* Dots Indicator - Now on Left */}
                                                <div className="flex gap-2 pl-2">
                                                    {steps.map((_, index) => (
                                                        <button
                                                            key={index}
                                                            className={cn(
                                                                "h-1.5 rounded-full transition-all duration-300",
                                                                index === selectedIndex
                                                                    ? "bg-primary w-6"
                                                                    : "bg-gray-200 dark:bg-gray-700 w-1.5 hover:bg-primary/50"
                                                            )}
                                                            onClick={() => emblaApi && emblaApi.scrollTo(index)}
                                                        />
                                                    ))}
                                                </div>

                                                {/* Next Action Only */}
                                                <button
                                                    className="text-sm font-bold text-primary hover:text-primary-dark transition-colors px-4 py-2"
                                                    onClick={handleNext}
                                                >
                                                    {t('onboarding.next')}
                                                </button>
                                            </Transition>
                                        </div>
                                    </div>

                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </Dialog>
            </Transition>

            <CreatePetDialog
                open={isPetDialogOpen}
                onClose={() => setIsPetDialogOpen(false)}
                onSuccess={(pet) => {
                    setIsPetDialogOpen(false);
                    // Optional: Navigate to pet? Left as generic close for now.
                }}
            />
        </Fragment>
    );
}
